import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calculator, Clock, DollarSign, TrendingUp, Download, Mail, Users, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ROIData {
  monthlyEmails: number;
  hoursSaved: number;
  costSaved: number;
  revenueGenerated: number;
  roi: number;
}

const ROICalculator: React.FC = () => {
  const [monthlyEmails, setMonthlyEmails] = useState([1000]);
  const [email, setEmail] = useState('');
  const [roiData, setROIData] = useState<ROIData | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { toast } = useToast();

  // Calculate ROI based on email volume
  const calculateROI = (emailVolume: number): ROIData => {
    // Realistic estimates based on actual outreach workflows
    const timePerEmailManual = 2; // minutes per email for manual outreach (research + personalization)
    const timePerEmailMunger = 0.2; // minutes per email with AI (setup + monitoring)
    const timeSavedPerEmail = timePerEmailManual - timePerEmailMunger;
    
    const hoursSaved = Math.round((emailVolume * timeSavedPerEmail) / 60);
    const hourlyRate = 50; // Average sales professional hourly rate
    const costSaved = Math.round(hoursSaved * hourlyRate);
    
    // Revenue calculations (conservative 1% conversion rate - more realistic)
    const conversionRate = 0.01;
    const avgDealSize = 2000; // More conservative average deal size
    const revenueGenerated = Math.round(emailVolume * conversionRate * avgDealSize);
    
    // ROI calculation (assuming $97/month Munger cost)
    const mungerCost = 97;
    const roi = Math.round(((costSaved + revenueGenerated - mungerCost) / mungerCost) * 100);

    return {
      monthlyEmails: emailVolume,
      hoursSaved,
      costSaved,
      revenueGenerated,
      roi
    };
  };

  useEffect(() => {
    const data = calculateROI(monthlyEmails[0]);
    setROIData(data);
  }, [monthlyEmails]);

  const handleCalculate = async () => {
    setIsCalculating(true);
    setShowResults(false);
    
    // Track ROI calculator usage with Meta Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'ROICalculatorUsed', {
        email_volume: monthlyEmails[0],
        content_category: 'roi_calculator',
        value: 50 // Assign value to ROI calculator usage
      });
    }
    
    // Simulate calculation delay for effect
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setShowResults(true);
    setIsCalculating(false);
  };

  const handleDownloadReport = async () => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email to download the ROI report.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Track lead generation with Meta Pixel
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Lead', {
          content_name: 'ROI Report Download',
          content_category: 'roi_calculator',
          value: 75, // Higher value for email capture
          email_volume: monthlyEmails[0],
          roi_calculated: roiData?.roi
        });
      }
      
      // Simulate PDF generation and email capture
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: "Report Generated!",
        description: "Your personalized ROI report has been sent to your email.",
      });

      // In production, this would generate and send an actual PDF
      // For now, redirect to signup with the calculated data
      const signupUrl = `https://auth.munger.ai/#/choose-plan?email=${encodeURIComponent(email)}&roi=${roiData?.roi}&emails=${monthlyEmails[0]}`;
      window.location.href = signupUrl;
      
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to generate report. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <section className="py-24 relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-transparent to-purple-100/20" />
      
      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 border border-green-200 mb-6">
            <Calculator className="w-5 h-5 text-green-600" />
            <span className="text-sm font-semibold text-green-700">ROI Calculator</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Calculate Your Savings
            </span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            See how much your autonomous AI marketer can save vs manual outreach
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Calculator Input */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Card className="bg-white border-gray-200 shadow-xl hover:shadow-2xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100">
                <CardTitle className="text-2xl text-gray-800 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-blue-600" />
                  Your Email Volume
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8 p-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-gray-600">
                      Monthly Emails Sent
                    </label>
                    <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {monthlyEmails[0].toLocaleString()}
                    </span>
                  </div>
                  
                  <Slider
                    value={monthlyEmails}
                    onValueChange={setMonthlyEmails}
                    max={10000}
                    min={100}
                    step={100}
                    className="w-full"
                  />
                  
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>100</span>
                    <span>5,000</span>
                    <span>10,000+</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <div className="text-xs text-gray-600">Your Time Per Email</div>
                    <div className="text-sm font-bold text-gray-800">2min → 0.2min</div>
                  </div>
                  
                  <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <div className="text-xs text-gray-600">Conversion Rate</div>
                    <div className="text-sm font-bold text-gray-800">1.0%</div>
                  </div>
                  
                  <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                    <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <div className="text-xs text-gray-600">Avg Deal Size</div>
                    <div className="text-sm font-bold text-gray-800">$2,000</div>
                  </div>
                </div>

                <Button 
                  onClick={handleCalculate}
                  disabled={isCalculating}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {isCalculating ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Calculating...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Calculate My ROI
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {roiData && (
              <>
                {/* Main ROI Card */}
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-xl overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200/30 to-emerald-200/30 rounded-full blur-3xl" />
                  <CardContent className="p-8 text-center relative">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 border border-green-200 mb-4">
                      <TrendingUp className="w-5 h-5 text-green-700" />
                      <span className="text-sm font-semibold text-green-700">Total ROI</span>
                    </div>
                    
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={showResults ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="text-6xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2"
                    >
                      {roiData.roi.toLocaleString()}%
                    </motion.div>
                    
                    <p className="text-gray-600">
                      Return on Investment in the first month
                    </p>
                  </CardContent>
                </Card>

                {/* Breakdown Cards */}
                <div className="grid gap-4">
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Clock className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Time Saved</div>
                            <div className="text-2xl font-bold text-gray-800">
                              {roiData.hoursSaved} hours/month
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Value</div>
                          <div className="text-xl font-bold text-blue-600">
                            ${roiData.costSaved.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Revenue Generated</div>
                            <div className="text-2xl font-bold text-gray-800">
                              ${roiData.revenueGenerated.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">From</div>
                          <div className="text-xl font-bold text-purple-600">
                            {Math.round(monthlyEmails[0] * 0.01)} deals
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Download Report */}
                <Card className="bg-white border-gray-200 shadow-xl">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">
                        Get Your Detailed ROI Report
                      </h4>
                      <p className="text-sm text-gray-600">
                        Receive a comprehensive PDF breakdown of your potential savings
                      </p>
                    </div>
                    
                    <div className="flex gap-4">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email for the report"
                        className="flex-1 bg-gray-50 border-gray-300 text-gray-800 placeholder:text-gray-400 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <Button
                        onClick={handleDownloadReport}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-gray-700 mb-6">
            Ready to achieve these results for your business?
          </p>
          <Button
            onClick={() => {
              // Track plan interest with Meta Pixel
              if (typeof window !== 'undefined' && (window as any).fbq) {
                (window as any).fbq('track', 'AddToWishlist', {
                  content_name: 'Free Trial',
                  content_category: 'subscription',
                  value: 97,
                  source: 'roi_calculator'
                });
              }
              window.location.href = 'https://auth.munger.ai/#/choose-plan';
            }}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            Start Your Free Trial
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator; 